import React, { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  Content,
  Event,
} from '@amzn/coral_com-amazon-ihmlearninganddevelopmentservice'
import Button from '@amzn/meridian/button'
import ProgressTrackerStep, { ProgressTrackerStepType } from '@amzn/meridian/progress-tracker/progress-tracker-step'
import Row from '@amzn/meridian/row'

import { store } from '@atredux/store'
import { getIsAnnouncementsFinished } from '@atredux/selectors/announcementsSelector'
import { setIsAnnouncementsFinished } from '@atredux/slices/announcementsSlice'
import { getIsConnectionsFinished } from '@atredux/selectors/connectionsSelector'
import IhmLearningAndDevelopmentServiceClient from '@api/IhmLearningAndDevelopmentServiceClient'
import { getStoreId } from '@src/utils/EnvironmentUtil'
import { AnnouncementsModal } from './AnnouncementsModal'
import ProgressTracker from '@amzn/meridian/progress-tracker'
import { getSelectedLocale } from '@src/redux/selectors/localeSelector'

// Index of the current creative being displayed.
let creativeIdx = -1

// Index of the current event being displayed.
let eventIdx = -1

const AnnouncementsModalController = () => {
    const [openModal, setOpenModal] = useState(false)
    const [currentEvent, setCurrentEvent] = useState<Event>(undefined)
    const [currentCreative, setCurrentCreative] = useState<Content>(undefined)
    const [events, setEvents] = useState<Event[]>(undefined)
    const [announcementStartTime, setAnnouncementStartTime] = useState(0)

    const isConnectionsFinished = useSelector(getIsConnectionsFinished)
    const isAnnouncementsFinished = useSelector(getIsAnnouncementsFinished)
    const selectedStore = useSelector(getStoreId)
    const locale = useSelector(getSelectedLocale)

    /**
     * Called when the associate completes the connections workflow.
     * If announcements are not finished, it calls the L&D service to get the announcements.
     */
    useEffect(() => {
        if (isConnectionsFinished && !isAnnouncementsFinished) {
            getAnnouncementsFromLD()
        }
    }, [isConnectionsFinished])

    /**
     * Called when the currentEvent state changes.
     * Sets the creativeIdx to 0 if the current event has creatives to display.
     * If the current event does not have any creatives, it moves to the next event if there is one.
     * Otherwise, it completes the announcement workflow.
     */
    useEffect(() => {
        if (!currentEvent) {
            return
        } else if (!currentEvent.contents || currentEvent.contents?.length <= 0) {
            console.log(`AnnouncementsModalController - current announcement, ${currentEvent.eventId}, has no content to show.`)
            hasNextEvent() ? moveToNextEvent() : setAnnouncementsFinished()
        } else {
            console.log(`AnnouncementsModalController - Starting announcement ${currentEvent.eventId}.`)
            creativeIdx = 0
            setAnnouncementStartTime(Date.now())
            setCurrentCreative(currentEvent.contents[creativeIdx])
        }
    }, [currentEvent])

    useEffect(() => {
        if (currentCreative) {
            console.log(`AnnouncementsModalController - displaying creative, ${currentCreative.creative?.creativeId}, in announcement ${currentEvent.eventId}.`)
            showModal()
        }
    }, [currentCreative])

    /**
     * Sets the openModal state to true if the openModal state is false.
     */
    const showModal = () => {
        if (!openModal) {
            setOpenModal(true)
        }
    }

    /**
     * Sets the current event to the event at eventIdx + 1
     * if eventIdx is less than the length of the events array.
     */
    const moveToNextEvent = () => {
        if (eventIdx < events?.length) {
            setCurrentEvent(events[++eventIdx])
        }
    }

    const eventsHaveCreativesToDisplay = (eventList: Event[]): boolean => {
        return eventList.some((event) => event.contents?.length > 0)
    }

    /**
     * Checks if there is another event to display.
     * @returns True if there is another event to display, false otherwise.
     */
    const hasNextEvent = (): boolean => {
        return eventIdx >= 0 && eventIdx < events.length - 1
    }

    /**
     * Checks if there is another creative to display in the current event.
     * @returns True if there is another creative to display, false otherwise.
     */
    const hasNextCreativeInEvent = (): boolean => {
        return creativeIdx >= 0 && creativeIdx < currentEvent.contents.length - 1
    }

    /**
     * Checks if there are previous creatives in the current Event.
     * @returns True if there are previous creatives in the current Event, false otherwise.
     */
    const hasPrevCreativeInEvent = (): boolean => {
        return creativeIdx > 0
    }

    /**
     * Sets the current creative to the creative at creativeIdx + 1 if there is another
     * creative to display in the current event. If there are no more creatives, it set's the
     * current event to eventIdx + 1. If there are no more events to display, it
     * finishes the announcement workflow.
     */
    const moveToNextCreative = async () => {
        if (hasNextCreativeInEvent()) {
            console.log(`AnnouncementsModalController - moving to next creative in announcement ${currentEvent.eventId}.`)
            setCurrentCreative(currentEvent.contents[++creativeIdx])
        } else if (hasNextEvent()) {
            console.log(`AnnouncementsModalController - no more creatives found in announcement ${currentEvent.eventId}. Moving to next announcement.`)
            await completeAnnouncement()
            moveToNextEvent()
        } else {
            console.log('AnnouncementsModalController - Finished viewing all announcements.')
            await completeAnnouncement()
            setAnnouncementsFinished()
        }
    }

    /**
     * Sets the current creative to the creative at creativeIdx - 1.
     * Callers of this method are responsible for ensuring that there are previous
     * creatives to display in the current event.
     */
    const moveToPrevCreative = () => {
        setCurrentCreative(currentEvent.contents[--creativeIdx])
    }

    /**
     * Called by the TrainingModal component when the user clicks on the
     * close button to skip videos, quizzes, or surveys. Finishes the announcement
     * workflow by setting the isAnnouncementsFinished redux state variable to true.
     */
    const clearTrainingContent = () => {
        setAnnouncementsFinished()
    }

    /**
     * Sets the isAnnouncementsFinished redux state to true and closes the modal.
     */
    const setAnnouncementsFinished = () => {
        console.log('AnnouncementsModalController - announcements workflow completed.')
        setOpenModal(false)
        store.dispatch(setIsAnnouncementsFinished({ isAnnouncementsFinished: true }))
    }

    /**
     * Calls L&D to get the announcements to display to the user.
     * Sets the events and currentEvent state variables if L&D
     * returns announcements to display.
     *
     * Finishes the announcement workflow if:
     *   1. L&D's GetAnnouncements API throws an error.
     */
    const getAnnouncementsFromLD = async () => {
        console.log(`AnnouncementsModalController - Retrieving announcements for store: ${selectedStore}, locale: ${locale}`)
        await IhmLearningAndDevelopmentServiceClient.getInstance().getAnnouncements(selectedStore, locale)
            .then((response) => {
                const eventList = response.events
                if (eventList && eventList.length > 0 && eventsHaveCreativesToDisplay(eventList)) {
                    console.log(`AnnouncementsModalController - L&D returned ${eventList.length} announcements.`)
                    setEvents(eventList)
                    eventIdx = 0
                    setCurrentEvent(eventList[eventIdx])
                } else {
                    console.log('AnnouncementsModalController - L&D returned 0 announcements.')
                    setAnnouncementsFinished()
                }
            })
            .catch((error) => {
                console.error(`AnnouncementsModalController - L&D.getAnnouncements threw an error: ${JSON.stringify(error)}`)
                setAnnouncementsFinished()
            })
    }

    /**
     * Calls the L&D completeTrainingEvent API to indicate that the user has
     * viewed all of the creatives in the current announcement event.
     */
    const completeAnnouncement = async () => {
        await IhmLearningAndDevelopmentServiceClient.getInstance().completeTrainingEvent(selectedStore, currentEvent.eventId, announcementStartTime, Date.now())
            .then(() => {
                console.log(`AnnouncementsModalController - Successfully recorded completion of announcement: ${currentEvent.eventId}.`)
            })
            .catch((error) => {
                console.error(`AnnouncementsModalController - Failed to record completion of announcement: ${currentEvent.eventId}. Error: ${JSON.stringify(error)}`)
            })
    }

    /**
     * Returns the navigation buttons to display in the AnnouncementModal based on
     * the current event and current creative.
     */
    const renderButton = (canAdvance: boolean) => {
        if (hasNextCreativeInEvent() && hasPrevCreativeInEvent()) {
            return (
                <Row alignmentHorizontal="justify" minWidth="95%" widths={['50%', '50%']} spacingInset="small small">
                    <Button data-testid="announcement-creative-back-button" type="link" onClick={moveToPrevCreative}>
                        Back
                    </Button>
                    <Button data-testid="announcement-creative-next-button" onClick={moveToNextCreative} disabled={!canAdvance}>
                        Next
                    </Button>
                </Row>
            )
        } else if (hasNextCreativeInEvent() && !hasPrevCreativeInEvent()) {
            return (
                <Row alignmentHorizontal="justify" minWidth="95%" widths={['100%']} spacingInset="small small">
                    <Button data-testid="announcement-creative-next-button" onClick={moveToNextCreative} disabled={!canAdvance}>
                        Next
                    </Button>
                </Row>
            )
        } else if (!hasNextCreativeInEvent() && hasPrevCreativeInEvent()) {
            return (
                <Row alignmentHorizontal="justify" minWidth="95%" widths={['50%', '50%']} spacingInset="small small">
                    <Button data-testid="announcement-creative-back-button" type="link" onClick={moveToPrevCreative}>
                        Back
                    </Button>
                    <Button data-testid="announcement-creative-done-button" onClick={moveToNextCreative} disabled={!canAdvance}>
                        Done
                    </Button>
                </Row>
            )
        } else {
            return (
                <Row alignmentHorizontal="justify" minWidth="95%" widths={['100%']} spacingInset="small small">
                    <Button data-testid="announcement-creative-done-button" onClick={moveToNextCreative} disabled={!canAdvance}>
                        Done
                    </Button>
                </Row>
            )
        }
    }

    /**
     * Mapping of strings to ProgressTrackerStepType values which is used
     * by the ProgressTracker component.
     */
    const ProgressTrackerStepTypeMapping: {[key: string]: ProgressTrackerStepType} = {
        future: 'future',
        past: 'past',
        present: 'present',
    }

    /**
     * Returns an array of strings representing the progress of the current event.
     * The array contains 'past' for each creative that has been viewed, 'present'
     * for the current creative, and 'future' for each creative that has not been viewed.
     * @returns An array of strings representing the progress of the current event.
     */
    const getProgressData = (): string[] => {
        const progressData: string[] = []
        if (creativeIdx === -1) {
            return progressData
        }

        for (let i = 0; i < creativeIdx; ++i) {
            progressData.push('past')
        }

        progressData.push('present')

        for (let i = creativeIdx + 1; i < currentEvent.contents.length; ++i) {
            progressData.push('future')
        }

        return progressData
    }

    /**
     * Renders the ProgressTracker component which indicates to the user how many creatives
     * are in the announcement event and which creative they are currently viewing.
     * @returns The ProgressTracker component or null if there is only one creative to display.
     */
    const renderProgressTracker = (): ReactElement | null => {
        const progressData = getProgressData()
        if (progressData.length <= 1) {
            return null
        }

        return (
            <ProgressTracker data-testid="announcement-progress-tracker" direction="row" compact={true} type="neutral">
            {progressData.map((progress: string, index: number) => {
                return (
                    <ProgressTrackerStep
                        data-testid={`announcement-${progress}-${index}`}
                        key={`${progress}-${index}`}
                        type={ProgressTrackerStepTypeMapping[progress]}
                        label={progress}
                        showLabel={false}
                        alert="none"
                        trackLength={0}
                    />
                )
            })}
            </ProgressTracker>
        )
    }

    return (
      <div data-testid="announcement-modal">
        {openModal &&
            <AnnouncementsModal
                announcementContent={currentCreative}
                renderButton={renderButton}
                renderProgressTracker={renderProgressTracker}
                clearAnnouncementContent={clearTrainingContent}
            />
        }
      </div>
    )
}

export default AnnouncementsModalController
